import UserInfo from "../userInfo";
import useCalendarEvents from "./useCalendarEvents";
import useDeliveries from "./useDeliveries";
import useDropoffGates from "./useDropoffGates";
import useDropoffs from "./useDropoffs";
import useEquipment from "./useEquipment";
import useGates from "./useGates";
import useGateToDropoffRoutes from "./useGateToDropoffRoutes";
import useHaulRoutes from "./useHaulRoutes";
import useMapAreas from "./useMapAreas";
import useMapInfo from "./useMapInfo";
import useMapObjects from "./useMapObjects";
import useTeams from "./useTeams";
import useVendors from "./useVendors";

interface useProjectInfoProps {
    userInfo: UserInfo | null;
    projects: any[];
}

const useProjectInfo = ({ userInfo, projects }: useProjectInfoProps) => {
    const projectInfo = (projectId: string | number | null): any | null => {
        if (!projectId) {
            return null;
        }

        return (
            projects.find((projectInfo) => projectInfo.id === projectId || projectInfo.unique_token == projectId) ||
            null
        );
    };

    const { mapObjectsByProject, mapObjectsForProject, refreshMapObjectsByProject } = useMapObjects({
        projects,
        projectInfo,
    });

    const { mapAreasByProject, mapAreasForProject, refreshMapAreasByProject } = useMapAreas({
        projects,
        projectInfo,
    });

    const { equipmentByProject, equipmentForProject, refreshEquipmentByProject } = useEquipment({
        projects,
        projectInfo,
    });

    const { dropoffsByProject, dropoffsForProject, refreshDropoffsByProject } = useDropoffs({
        projects,
        projectInfo,
    });

    const { gatesByProject, gatesForProject, refreshGatesByProject } = useGates({
        projects,
        projectInfo,
    });

    const { haulRoutesByProject, haulRoutesForProject, refreshHaulRoutesByProject } = useHaulRoutes({
        projects,
        projectInfo,
    });

    const { gateToDropoffRoutesByProject, gateToDropoffRoutesForProject, refreshGateToDropoffRoutesByProject } =
        useGateToDropoffRoutes({
            projects,
            projectInfo,
        });

    const { dropoffGatesByProject, dropoffGatesForProject, refreshDropoffGatesByProject } = useDropoffGates({
        projects,
        projectInfo,
    });

    const {
        mapRef,
        mapGeoJson,
        setMapGeoJson,
        mapPopup,
        mapBoxDraw,
        setMapBoxDraw,
        currentMappable,
        setCurrentMappable,
        currentMapObject,
        setCurrentMapObject,
        editCurrentMappable,
        setEditCurrentMappable,
        showOtherMapItemsWhileEditing,
        setShowOtherMapItemsWhileEditing,
        showGeotiff,
        setShowGeotiff,
        newMappableType,
        setNewMappableType,
        pendingGateToDropoffRoute,
        setPendingGateToDropoffRoute,
        editCurrentMappablePosition,
        setEditCurrentMappablePosition,
        settingJobsiteCoordinates,
        setSettingJobsiteCoordinates,
        mapFilterDate,
        setMapFilterDate,
        currentWaypointStep,
        setCurrentWaypointStep,
        hoveredMapObject,
        setHoveredMapObject,
        hoveredDropoffGatePair,
        setHoveredDropoffGatePair,
        mapBounds,
        userLocation,
        setUserLocation,
        geoControlRef,
        waypointGuidance,
        setWaypointGuidance,
    } = useMapInfo();

    const { teamsByProject, teamsForProject, refreshTeamsByProject } = useTeams({
        projects,
        projectInfo,
    });

    const { vendorsByProject, vendorsForProject, refreshVendorsByProject } = useVendors({
        projects,
        projectInfo,
    });

    const { createDelivery, updateDelivery, deleteDelivery } = useDeliveries({
        projectInfo,
    });

    const {
        calendarEventsFilterInfo,
        setCalendarEventsFilterInfo,
        refreshCalendarEventsByProject,
        calendarEventsByProject,
        calendarEventsForProject,
        updateCalendarEventInState,
        createCalendarEvent,
        updateCalendarEvent,
        deleteCalendarEvent,
    } = useCalendarEvents({
        userInfo,
        projects,
        projectInfo,
    });

    return {
        projectInfo,
        mapObjectsByProject,
        mapObjectsForProject,
        refreshMapObjectsByProject,
        mapAreasByProject,
        mapAreasForProject,
        refreshMapAreasByProject,
        equipmentByProject,
        equipmentForProject,
        refreshEquipmentByProject,
        dropoffsByProject,
        dropoffsForProject,
        refreshDropoffsByProject,
        gatesByProject,
        gatesForProject,
        refreshGatesByProject,
        haulRoutesByProject,
        haulRoutesForProject,
        refreshHaulRoutesByProject,
        gateToDropoffRoutesByProject,
        gateToDropoffRoutesForProject,
        refreshGateToDropoffRoutesByProject,
        dropoffGatesByProject,
        dropoffGatesForProject,
        refreshDropoffGatesByProject,
        teamsByProject,
        teamsForProject,
        refreshTeamsByProject,
        vendorsByProject,
        vendorsForProject,
        refreshVendorsByProject,
        createDelivery,
        updateDelivery,
        deleteDelivery,
        mapRef,
        mapGeoJson,
        setMapGeoJson,
        mapPopup,
        mapBoxDraw,
        setMapBoxDraw,
        currentMappable,
        setCurrentMappable,
        currentMapObject,
        setCurrentMapObject,
        editCurrentMappable,
        setEditCurrentMappable,
        showOtherMapItemsWhileEditing,
        setShowOtherMapItemsWhileEditing,
        showGeotiff,
        setShowGeotiff,
        newMappableType,
        setNewMappableType,
        pendingGateToDropoffRoute,
        setPendingGateToDropoffRoute,
        editCurrentMappablePosition,
        setEditCurrentMappablePosition,
        settingJobsiteCoordinates,
        setSettingJobsiteCoordinates,
        mapFilterDate,
        setMapFilterDate,
        currentWaypointStep,
        setCurrentWaypointStep,
        hoveredMapObject,
        setHoveredMapObject,
        hoveredDropoffGatePair,
        setHoveredDropoffGatePair,
        mapBounds,
        userLocation,
        setUserLocation,
        geoControlRef,
        waypointGuidance,
        setWaypointGuidance,
        calendarEventsFilterInfo,
        setCalendarEventsFilterInfo,
        refreshCalendarEventsByProject,
        calendarEventsByProject,
        calendarEventsForProject,
        updateCalendarEventInState,
        createCalendarEvent,
        updateCalendarEvent,
        deleteCalendarEvent,
    };
};

export default useProjectInfo;
